/**
 * Garde de cycle de vie pour les animations de sprites.
 *
 * ── Pourquoi ce module existe ────────────────────────────────────────────────
 *
 * Le moteur détruit et recrée des sprites en cours de route — amélioration de palier,
 * rotation de vue, resynchronisation, élagage des routes — et c'est **assumé** : « un
 * sprite `destroyed` vaut absent, l'état de jeu fait foi » (§8). Mais un tween lancé
 * avant la destruction continue de tourner : son `onUpdate` écrit alors dans un objet
 * dont PixiJS a déjà libéré les champs, et lève une `TypeError` **à l'intérieur du
 * ticker**. Ce n'est pas une animation ratée : c'est la boucle de rendu entière qui
 * s'arrête, jeu figé.
 *
 * Le défaut est apparu deux fois, à deux endroits, pour la même raison — d'où ce helper
 * partagé plutôt qu'une garde recopiée : `playPlacementJuice` (chute, écrasement,
 * anneau) et `syncRoads` (déroulement du tracé, > 1 s pendant laquelle une vente peut
 * élaguer la route).
 *
 * Corollaire : **tout `onUpdate` qui touche un sprite passe par ici.** Un tween qui
 * écrit directement dans un sprite est un bug en attente.
 */

/** Tout objet PixiJS destructible — `Sprite`, `Graphics`, `Container`. */
export interface Destroyable {
  readonly destroyed: boolean;
}

/** Vrai si l'objet a été détruit et ne doit plus être touché. */
export function isGone(target: Destroyable): boolean {
  return target.destroyed;
}

/**
 * Enveloppe un callback d'animation : il ne s'exécute que si la cible est vivante.
 *
 * Rend un no-op silencieux plutôt qu'une erreur — la destruction est un événement
 * normal du moteur, pas une anomalie à signaler.
 */
export function whileAlive<T>(
  target: Destroyable,
  callback: (value: T) => void,
): (value: T) => void {
  return (value: T) => {
    if (!target.destroyed) callback(value);
  };
}
